import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Provider } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../environments/environment';

const UNSUPPORTED_INTEGRATIONS = new Set(['BrowserSession', 'BrowserTracing', 'Replay']);

const IGNORED_ERRORS: (string | RegExp)[] = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  'ChunkLoadError',
  /Loading chunk \d+ failed/i,
  /^AbortError/,
];

export function isSentryEnabled(): boolean {
  return !!environment.sentry.dsn;
}

export function initSentry(): void {
  if (!isSentryEnabled()) {
    if (!environment.production) {
      console.info('[Sentry] No DSN configured, error reporting is disabled.');
    }
    return;
  }

  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.sentry.release || undefined,
    debug: environment.sentry.debug,
    sampleRate: environment.sentry.sampleRate,

    tracesSampleRate: 0,
    sendClientReports: false,
    integrations: (defaults) => defaults.filter((i) => !UNSUPPORTED_INTEGRATIONS.has(i.name)),

    ignoreErrors: IGNORED_ERRORS,
    sendDefaultPii: false,

    beforeSend(event, hint) {
      if (hint?.originalException instanceof HttpErrorResponse) {
        return null;
      }
      return event;
    },
  });
}

export function provideSentryErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({ logErrors: !environment.production }),
  };
}

export function captureHttpError(err: HttpErrorResponse, method: string, url: string): void {
  if (!isSentryEnabled()) {
    return;
  }

  const errorCode =
    err.error && typeof err.error === 'object' && 'error_code' in err.error
      ? String((err.error as { error_code: unknown }).error_code)
      : undefined;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTransactionName(`${method} ${stripQuery(url)}`);
    scope.setFingerprint(['http', method, stripQuery(url), String(err.status), errorCode ?? '']);
    scope.setTags({
      http_method: method,
      http_status: err.status,
      ...(errorCode ? { api_error_code: errorCode } : {}),
    });
    scope.setContext('http', {
      method,
      url: stripQuery(url),
      status: err.status,
      statusText: err.statusText,
      error_code: errorCode,
    });
    Sentry.captureException(
      new Error(`HTTP ${err.status} ${method} ${stripQuery(url)}${errorCode ? ` (${errorCode})` : ''}`)
    );
  });
}

export function setSentryUser(username: string | null): void {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setUser(username ? { username } : null);
}

function stripQuery(url: string): string {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}
